import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { cn } from '@/shared/utils/cn';
import { useModalContext } from '../../context';
import { InnerButton } from '@/modules/InnerButton';
import {
  faArrowDownLeftAndArrowUpRightToCenter,
  faArrowUpRightAndArrowDownLeftFromCenter,
} from '@fortawesome/pro-regular-svg-icons';
import { IconType } from '@/shared/types/types';

export interface ModalImageProps {
  /** URL de la imagen en miniatura; sin `expandedSrc` se usa también en la vista expandida. */
  src: string;
  /**
   * URL opcional con mayor resolución para la vista expandida en `thumbnailMode`.
   * Si no se define, se usa `src` también al expandir (comportamiento anterior).
   */
  expandedSrc?: string;
  alt?: string;
  className?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  thumbnailMode?: boolean;
  thumbnailSize?: 'sm' | 'md' | 'lg' | 'xl' | 'auto';
  expandButtonIcon?: IconType;
  padding?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  aspect?: 'square' | 'video' | 'portrait' | 'landscape' | 'none';
}

export const ModalImage: React.FC<ModalImageProps> = ({
  src,
  expandedSrc,
  alt = '',
  className,
  objectFit = 'cover',
  thumbnailMode = false,
  thumbnailSize = 'auto',
  expandButtonIcon = 'ImageOutlined',
  padding = 'md',
  aspect = 'none',
}) => {
  const expandedImageSrc = expandedSrc ?? src;
  const { hideFooter, currentTab } = useModalContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousTabRef = useRef<string | undefined>(currentTab);
  const [modalContainer, setModalContainer] = useState<HTMLElement | null>(null);
  const [modalDimensions, setModalDimensions] = useState<{ width: number; height: number } | null>(
    null
  );
  const [modalPosition, setModalPosition] = useState<{ top: number; left: number } | null>(null);
  const [modalElement, setModalElement] = useState<HTMLElement | null>(null);
  const [bodyElement, setBodyElement] = useState<HTMLElement | null>(null);

  // Función para actualizar dimensiones y posición
  const updateDimensions = useCallback((modalEl: HTMLElement, bodyEl: HTMLElement) => {
    const modalRect = modalEl.getBoundingClientRect();
    const bodyRect = bodyEl.getBoundingClientRect();

    const relativeTop = bodyRect.top - modalRect.top;
    const relativeLeft = bodyRect.left - modalRect.left;

    setModalDimensions({
      width: bodyRect.width,
      height: bodyRect.height,
    });

    setModalPosition({
      top: relativeTop,
      left: relativeLeft,
    });
  }, []);

  // Encontrar el ModalBody en ancestros (`data-imj-modal-body`)
  useEffect(() => {
    if (isExpanded && containerRef.current) {
      // Buscar el ModalBody (`data-imj-modal-body`, estable ante sectionPadding md/sm)
      let parent = containerRef.current.parentElement;
      let level = 0;

      while (parent && level < 10) {
        if (parent.hasAttribute('data-imj-modal-body')) {
          setModalContainer(parent);
          setBodyElement(parent);

          // Obtener las dimensiones del modal completo (subir un nivel más para el contenedor del modal)
          let modalParent = parent.parentElement;
          let modalLevel = 0;

          while (modalParent && modalLevel < 10) {
            if (
              (modalParent.classList.contains('ui-rounded-surface-modal') ||
                modalParent.classList.contains('ui-rounded-lg')) &&
              modalParent.classList.contains('ui-bg-fill') &&
              modalParent.classList.contains('ui-flex')
            ) {
              setModalElement(modalParent);

              const modalRect = modalParent.getBoundingClientRect();
              const bodyRect = parent.getBoundingClientRect();

              // Actualizar dimensiones iniciales
              updateDimensions(modalParent, parent);
              break;
            }
            modalParent = modalParent.parentElement;
            modalLevel++;
          }
          break;
        }
        parent = parent.parentElement;
        level++;
      }
    } else if (!isExpanded && !isClosing) {
      setModalContainer(null);
      setModalDimensions(null);
      setModalPosition(null);
      setModalElement(null);
      setBodyElement(null);
    }
  }, [isExpanded, isClosing, updateDimensions]);

  // Agregar position: relative al ModalBody cuando la imagen está expandida
  useEffect(() => {
    if (isExpanded && modalContainer) {
      // Agregar position: relative al ModalBody para que el absolute funcione correctamente
      const originalPosition = window.getComputedStyle(modalContainer).position;
      if (originalPosition === 'static' || originalPosition === '') {
        modalContainer.style.position = 'relative';
      }

      return () => {
        // Restaurar la posición original cuando se cierra
        if (originalPosition === 'static' || originalPosition === '') {
          modalContainer.style.position = '';
        }
      };
    }
  }, [isExpanded, modalContainer, updateDimensions]);

  // Observar cambios en el tamaño del modal y actualizar dimensiones
  useEffect(() => {
    if (!isExpanded || !modalElement || !bodyElement) return;

    const resizeObserver = new ResizeObserver(() => {
      updateDimensions(modalElement, bodyElement);
    });

    // Observar tanto el modal como el ModalBody para detectar cambios
    resizeObserver.observe(modalElement);
    resizeObserver.observe(bodyElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isExpanded, modalElement, bodyElement, updateDimensions]);

  const thumbnailSizes = {
    sm: 'ui-w-32 ui-h-32',
    md: 'ui-w-40 ui-h-40',
    lg: 'ui-w-48 ui-h-48',
    xl: 'ui-w-64 ui-h-64',
    auto: 'ui-w-full ui-h-auto',
  };

  const paddingSizes = {
    none: '',
    xs: 'ui-p-4',
    sm: 'ui-p-8',
    md: 'ui-p-12',
    lg: 'ui-p-16',
    xl: 'ui-p-20',
    '2xl': 'ui-p-24',
  };

  const aspectClasses = {
    square: 'ui-aspect-square',
    video: 'ui-aspect-video',
    portrait: 'ui-aspect-[3/4]',
    landscape: 'ui-aspect-[4/3]',
    none: '',
  };

  // Valores numéricos de los aspect ratios (estable)
  const aspectRatios = useMemo<Record<string, number>>(
    () => ({
      square: 1,
      video: 16 / 9,
      portrait: 3 / 4,
      landscape: 4 / 3,
      none: 0,
    }),
    []
  );

  // Calcular dimensiones de la imagen expandida manteniendo el aspect ratio
  const getExpandedImageDimensions = useCallback(() => {
    if (!modalDimensions || aspect === 'none') {
      return { width: '100%', height: '100%' };
    }

    const { width: containerWidth, height: containerHeight } = modalDimensions;

    // Padding externo (16px * 2 = 32px) + Padding interno del borde (16px * 2 = 32px)
    const totalPadding = 64;

    const availableWidth = containerWidth - totalPadding;
    const availableHeight = containerHeight - totalPadding;
    const targetRatio = aspectRatios[aspect];

    // Calcular dimensiones que respeten el aspect ratio sin exceder el contenedor
    let finalWidth: number;
    let finalHeight: number;

    // Intentar llenar el ancho primero
    finalWidth = availableWidth;
    finalHeight = finalWidth / targetRatio;

    // Si excede la altura disponible, ajustar por altura
    if (finalHeight > availableHeight) {
      finalHeight = availableHeight;
      finalWidth = finalHeight * targetRatio;
    }

    return {
      width: `${finalWidth}px`,
      height: `${finalHeight}px`,
    };
  }, [modalDimensions, aspect, aspectRatios]);

  // Función para abrir
  const handleOpen = useCallback(() => {
    setIsClosing(false);
    setIsExpanded(true);
  }, []);

  // Función para cerrar con animación
  const handleClose = useCallback(() => {
    setIsClosing(true);
    // Esperar a que termine la animación antes de cerrar
    setTimeout(() => {
      setIsExpanded(false);
      setIsClosing(false);
    }, 300); // Duración de la animación
  }, []);

  // Detectar cambios en el tab y cerrar la imagen expandida
  useEffect(() => {
    if (currentTab !== undefined && previousTabRef.current !== undefined) {
      if (currentTab !== previousTabRef.current && isExpanded) {
        // El tab cambió y la imagen está expandida, cerrarla
        handleClose();
      }
    }
    previousTabRef.current = currentTab;
  }, [currentTab, isExpanded, handleClose]);

  return (
    <div ref={containerRef} className={cn('ui-relative', className)}>
      {/* Thumbnail - siempre visible cuando está en modo thumbnail */}
      {thumbnailMode && (
        <div
          className={cn(
            'ui-relative',
            thumbnailSize === 'auto' ? 'ui-w-full' : 'ui-inline-block',
            isExpanded && 'ui-pointer-events-none ui-opacity-0'
          )}
        >
          <div
            className={cn(
              'ui-group ui-relative ui-cursor-pointer ui-overflow-hidden ui-rounded-lg ui-border ui-border-default',
              thumbnailSizes[thumbnailSize],
              // Aplicar aspect ratio solo cuando thumbnailSize es 'auto' y aspect no es 'none'
              thumbnailSize === 'auto' && aspect !== 'none' && aspectClasses[aspect]
            )}
          >
            <img
              src={src}
              alt={alt}
              className={cn(
                'ui-object-cover',
                // Si hay aspect ratio, la imagen debe llenar el contenedor completamente
                thumbnailSize === 'auto' && aspect !== 'none'
                  ? 'ui-h-full ui-w-full'
                  : thumbnailSize === 'auto'
                    ? 'ui-h-auto ui-w-full'
                    : 'ui-h-full ui-w-full ui-transition-transform group-hover:ui-scale-110'
              )}
            />
            {/* Botón de expansión */}
            {thumbnailSize === 'auto' ? (
              // Para tamaño auto: botón en esquina superior derecha, sin scale
              <div className="ui-absolute ui-right-4 ui-top-4 ui-z-10">
                <InnerButton
                  onClick={handleOpen}
                  icon={faArrowUpRightAndArrowDownLeftFromCenter}
                  theme="solid"
                  color="secondary"
                  size="xs"
                  pill="pill"
                  className="ui-bg-fill/90 ui-opacity-0 ui-shadow-04 ui-transition-opacity hover:ui-bg-fill group-hover:ui-opacity-100"
                />
              </div>
            ) : (
              // Para otros tamaños: botón centrado con overlay, con scale
              <div className="ui-bg-black/0 group-hover:ui-bg-black/30 ui-absolute ui-inset-0 ui-flex ui-items-center ui-justify-center ui-transition-colors">
                <InnerButton
                  onClick={handleOpen}
                  icon={faArrowUpRightAndArrowDownLeftFromCenter}
                  theme="solid"
                  color="secondary"
                  size="xs"
                  pill="pill"
                  className="ui-bg-fill/90 ui-opacity-0 ui-shadow-04 ui-transition-opacity hover:ui-bg-fill group-hover:ui-opacity-100"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Overlay con blur que cubre solo el ModalBody */}
      {(isExpanded || isClosing) && modalContainer
        ? ReactDOM.createPortal(
            <div
              className={cn(
                'ui-bg-black/20 ui-absolute ui-inset-0 ui-z-40 ui-backdrop-blur-md',
                'ui-transition-opacity ui-duration-300 ui-ease-in-out'
              )}
              style={{
                // Cubrir exactamente el área del ModalBody
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                bottom: '0',
                animation: isClosing ? 'fadeOut 0.3s ease-in-out' : 'fadeIn 0.3s ease-in-out',
              }}
              aria-hidden="true"
            />,
            modalContainer
          )
        : null}

      {/* Imagen expandida renderizada con portal en el ModalBody */}
      {(isExpanded || isClosing) && modalContainer && modalDimensions && modalPosition
        ? ReactDOM.createPortal(
            <div
              className={cn(
                'ui-bg-black/50 ui-absolute ui-z-50 ui-flex ui-items-center ui-justify-center ui-backdrop-blur-sm'
                // Cubrir solo el área del ModalBody y centrar contenido
              )}
              onClick={handleClose}
              style={{
                // Posicionar en el inicio del ModalBody (top: 0, left: 0 relativo al ModalBody)
                // Ahora que el ModalBody tiene position: relative, podemos usar top: 0 y left: 0
                top: '0',
                left: '0',
                right: '0',
                bottom: '0',
                // Usar 100% para cubrir todo el ModalBody y mantener centrado
                width: '100%',
                height: '100%',
                // Asegurar que el contenido esté centrado
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: isClosing
                  ? 'fadeOutScale 0.3s ease-in-out'
                  : 'fadeInScale 0.3s ease-in-out',
              }}
              ref={(el) => {
                // Ref callback para el elemento del portal
              }}
            >
              {/* Contenedor exterior con padding (espacio entre ModalBody y borde) */}
              <div
                className={cn(
                  'ui-relative',
                  // Ocupar todo el espacio disponible
                  'ui-h-full ui-w-full',
                  // Padding externo de 16px
                  'ui-p-16'
                )}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  height: '100%',
                  animation: isClosing
                    ? 'fadeOutScale 0.3s ease-in-out'
                    : 'fadeInScale 0.3s ease-in-out',
                }}
              >
                {/* Contenedor con borde */}
                <div
                  className={cn(
                    'ui-relative',
                    // Ocupar todo el espacio disponible
                    'ui-h-full ui-w-full',
                    // Usar flex para centrar la imagen
                    'ui-flex ui-items-center ui-justify-center',
                    // Borde según diseño de Figma
                    'ui-border ui-border-default',
                    // Padding interno de 16px
                    'ui-p-16',
                    // Border radius de 8px
                    'ui-rounded-lg',
                    // Fondo blanco
                    'ui-bg-fill',
                    // Overflow hidden para evitar desbordes
                    'ui-overflow-hidden'
                  )}
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {/* Contenedor con aspect ratio definido - incluye imagen y botón */}
                  <div
                    className={cn(
                      'ui-relative ui-overflow-hidden',
                      // Aplicar border radius a la imagen
                      'ui-rounded-lg',
                      'ui-shadow-03'
                    )}
                    style={{
                      // Usar dimensiones calculadas que respetan el aspect ratio
                      ...getExpandedImageDimensions(),
                    }}
                  >
                    {/* Imagen que llena el contenedor con aspect ratio */}
                    <img
                      src={expandedImageSrc}
                      alt={alt}
                      className="ui-h-full ui-w-full ui-object-cover"
                      style={{
                        objectFit: 'cover',
                        objectPosition: 'center center',
                        display: 'block',
                      }}
                    />

                    {/* Botón de minimizar/contraer - dentro del contenedor de la imagen */}
                    <div
                      className="ui-absolute ui-right-4 ui-top-4 ui-z-10"
                      style={{
                        animation: isClosing
                          ? 'fadeOut 0.3s ease-in-out'
                          : 'fadeIn 0.3s ease-in-out 0.1s both',
                      }}
                    >
                      <InnerButton
                        onClick={handleClose}
                        icon={faArrowDownLeftAndArrowUpRightToCenter}
                        theme="solid"
                        color="secondary"
                        size="xs"
                        pill="pill"
                        className="ui-bg-fill/90 ui-shadow-04 hover:ui-bg-fill"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>,
            modalContainer
          )
        : null}

      {/* Si no está en modo thumbnail, mostrar imagen normal */}
      {!thumbnailMode && (
        <div
          className={cn(
            'ui-relative ui-w-full ui-overflow-hidden ui-rounded-lg ui-transition-all ui-duration-300',
            'ui-h-[calc(100vh-280px)]',
            'ui-min-h-[400px]',
            'ui-max-h-[600px]',
            hideFooter && 'ui-rounded-b-lg',
            className
          )}
        >
          <img
            src={src}
            alt={alt}
            className={cn(
              'ui-h-full ui-w-full',
              objectFit === 'cover' && 'ui-object-cover',
              objectFit === 'contain' && 'ui-object-contain',
              objectFit === 'fill' && 'ui-object-fill',
              objectFit === 'none' && 'ui-object-none',
              objectFit === 'scale-down' && 'ui-object-scale-down'
            )}
            style={{
              objectFit: objectFit,
            }}
          />
        </div>
      )}
    </div>
  );
};

ModalImage.displayName = 'Modal.Image';
